import type { Namespace } from '../types';

// DE drafted — queued for native review in the task file's "DE review queue".
// "Grow Room(s)" / "Plumbing Room(s)" stay invariant (the spec HTML keeps
// "Grow Room" in its German copy — domain nouns).
export const iotControl = {
  ovTitle: { de: 'Übersicht', en: 'Overview' },
  ovDesc: {
    de: 'Farmweiter Status auf einen Blick. Zonen suchen oder filtern, dann eine öffnen, um ihre Aktoren zu steuern.',
    en: 'Farm-wide status at a glance. Search or filter zones, then open one to control its actuators.',
  },
  growTitle: { de: 'Grow Rooms', en: 'Grow Rooms' },
  growDesc: {
    de: 'Der Raum-Tab steuert den Raum; der Racks-Tab sucht und wählt Racks. Schalter zeigen den vom Controller bestätigten Relais-Zustand.',
    en: 'The Room tab controls the room; the Racks tab lets you search and select racks. Toggles reflect the real relay state confirmed by the controller.',
  },
  plumbTitle: { de: 'Plumbing Rooms', en: 'Plumbing Rooms' },
  plumbDesc: {
    de: 'Jeder Plumbing Room hat einen einzelnen Controller und keine Racks.',
    en: 'Each plumbing room has a single controller and no racks.',
  },

  all: { de: 'Alle', en: 'All' },
  allTypes: { de: 'Alle Typen', en: 'All types' },
  typeGrow: { de: 'Grow', en: 'Grow' },
  typePlumbing: { de: 'Plumbing', en: 'Plumbing' },
  typeElectrical: { de: 'Elektrik', en: 'Electrical' },
  noTypeRooms: { de: 'Noch keine Räume dieses Typs.', en: 'No rooms of this type yet.' },
  searchZones: { de: 'Zonen suchen… (Raum oder Rack)', en: 'Search zones… (room or rack)' },
  searchRooms: { de: 'Räume suchen…', en: 'Search rooms…' },

  kpiRooms: { de: 'Räume', en: 'Rooms' },
  kpiRacks: { de: 'Racks', en: 'Racks' },
  kpiActsOn: { de: 'Aktoren ein', en: 'Actuators on' },
  kpiCtrlsOnline: { de: 'Controller online', en: 'Controllers online' },
  nOfM: { de: '{n} von {m}', en: '{n} of {m}' },
  zones: { de: 'Zonen', en: 'Zones' },

  loadError: { de: 'Steuerung konnte nicht geladen werden', en: "Couldn't load controls" },
  emptyTitle: { de: 'Noch keine Räume zu steuern', en: 'No rooms to control yet' },
  emptyMsg: {
    de: 'Fügen Sie im Mapper Grow Rooms, Racks oder Plumbing Rooms hinzu und weisen Sie Mikrocontroller zu; ihre Aktoren erscheinen hier.',
    en: 'Add grow rooms, racks or plumbing rooms in the Mapper and assign microcontrollers; their actuators appear here.',
  },
  noMatchZones: { de: 'Keine passenden Zonen', en: 'No matching zones' },
  noMatchZonesMsg: {
    de: 'Keine Zonen entsprechen Suche und Filtern.',
    en: 'No zones match the current search and filters.',
  },
  noMatchGrow: { de: 'Keine passenden Grow Rooms', en: 'No matching grow rooms' },
  noMatchPlumb: { de: 'Keine passenden Plumbing Rooms', en: 'No matching plumbing rooms' },
  noMatchRoomsMsg: {
    de: 'Keine Räume entsprechen Suche und Filtern.',
    en: 'No rooms match the current search and filters.',
  },

  noController: { de: 'Kein Controller', en: 'No controller' },
  tabRoom: { de: 'Raum', en: 'Room' },
  tabRacks: { de: 'Racks', en: 'Racks' },
  tabNodes: { de: 'Nodes', en: 'Nodes' },
  readings: { de: 'Messwerte', en: 'Readings' },
  searchRacks: { de: 'Racks suchen…', en: 'Search racks…' },
  metaGrow_one: { de: 'Grow · {n} Rack', en: 'Grow · {n} rack' },
  metaGrow_many: { de: 'Grow · {n} Racks', en: 'Grow · {n} racks' },
  metaPlumb: { de: 'Plumbing · einzelner Controller', en: 'Plumbing · single controller' },
  metaPlumb_one: { de: 'Plumbing · {n} Node', en: 'Plumbing · {n} node' },
  metaPlumb_many: { de: 'Plumbing · {n} Nodes', en: 'Plumbing · {n} nodes' },
  actsOnSuffix: { de: 'von {total} Aktoren ein', en: 'of {total} actuators on' },
  openRoom: { de: 'Raum öffnen', en: 'Open room' },

  controls: { de: 'Steuerung', en: 'Controls' },
  nOn: { de: '{n} ein', en: '{n} on' },
  allOff: { de: 'Alle aus', en: 'All off' },
  noActs: { de: 'Keine Aktoren gemeldet.', en: 'No actuators reported.' },
  noReadings: { de: 'Keine Messwerte gemeldet.', en: 'No readings reported.' },
  stateOn: { de: 'EIN', en: 'ON' },
  stateOff: { de: 'AUS', en: 'OFF' },
  stateOffline: { de: 'OFFLINE', en: 'OFFLINE' },
  levelOnly: { de: 'nur Stufe', en: 'level only' },
  analogTitle: {
    de: 'Stufen-/Spektrum-Steuerung folgt mit der Firmware-Parameterspezifikation',
    en: 'Level / spectrum control arrives with the firmware parameter spec',
  },
  viewOnlyTitle: {
    de: 'Nur-Lesen — Aktor-Steuerung erfordert die Berechtigung actuator-control.',
    en: 'View only — controlling actuators needs the actuator-control permission.',
  },

  // Historical sensor trend (R-089)
  trendToggle: { de: 'Verlauf ein-/ausblenden: {label}', en: 'Show/hide trend: {label}' },
  trendTitle: { de: 'Verlauf · {label}', en: 'Trend · {label}' },
  seriesAvg: { de: 'Mittel', en: 'Average' },
  seriesMin: { de: 'Min', en: 'Min' },
  seriesMax: { de: 'Max', en: 'Max' },
  trendNoData: { de: 'Keine Daten', en: 'No data' },
  trendNoDataMsg: {
    de: 'Für diesen Zeitraum liegen keine Messwerte vor.',
    en: 'No readings in this range.',
  },
  trendError: { de: 'Verlauf nicht erreichbar', en: 'Trend unavailable' },
  trendErrorMsg: {
    de: 'Der Verlauf konnte nicht geladen werden. Bitte erneut versuchen.',
    en: 'The trend could not be loaded. Please retry.',
  },
  ariaToggle: { de: 'Umschalten: {label}', en: 'Toggle {label}' },
  ariaLevelOnly: {
    de: '{label} (Stufensteuerung nicht verfügbar)',
    en: '{label} (level control unavailable)',
  },
  cmdFailed: { de: 'Befehl fehlgeschlagen: {error}', en: 'Command failed: {error}' },
  dose: { de: 'Dosieren', en: 'Dose' },
  doseMlPlaceholder: { de: 'mL', en: 'mL' },
  doseSent: { de: '{ml} mL dosiert — {label}', en: 'Dosing {ml} mL — {label}' },
  doseConfirmTitle: { de: 'Dosierung starten?', en: 'Start dosing?' },
  doseConfirmMsg: {
    de: '{ml} mL über {label} ({where}) dosieren? Die Pumpe startet sofort.',
    en: 'Dose {ml} mL through {label} ({where})? The pump starts immediately.',
  },
  allOffConfirmTitle: { de: 'Alles ausschalten?', en: 'Switch everything off?' },
  allOffConfirmMsg_one: {
    de: '{n} laufender Aktor wird jetzt ausgeschaltet.',
    en: '{n} running actuator will be switched off now.',
  },
  allOffConfirmMsg_many: {
    de: '{n} laufende Aktoren werden jetzt ausgeschaltet.',
    en: '{n} running actuators will be switched off now.',
  },
  // A toggle whose confirmed state never came back within the timeout. Neither
  // "failed" nor "done" — the device simply did not answer.
  cmdUnconfirmed: {
    de: '{label}: Das Gerät hat den Befehl nicht bestätigt — angezeigt wird der zuletzt gemeldete Zustand.',
    en: '{label}: the device did not confirm the command — showing the last state it reported.',
  },

  // ── R-102 unified navigator + per-level pages ──
  title: { de: 'IoT-Steuerung', en: 'IoT Control' },
  navDesc: {
    de: 'Tunnel und Raum wählen. Grow- und Plumbing-Räume öffnen jeweils ihre eigenen Sensoren und Steuerungen.',
    en: 'Select a tunnel and a room. Grow and plumbing rooms each open to their own sensors and controls.',
  },
  // Navigator bar
  farm: { de: 'Farm', en: 'Farm' },
  tunnel: { de: 'Tunnel', en: 'Tunnel' },
  searchLabel: { de: 'Suche', en: 'Search' },
  typeLabel: { de: 'Typ', en: 'Type' },
  issueOffline: { de: 'offline', en: 'offline' },
  issueUnknown: { de: 'Status unbekannt', en: 'status unknown' },
  roomLabel: { de: 'Raum', en: 'Room' },
  searchNav: { de: 'Räume, Racks, Nodes suchen…', en: 'Search rooms, racks, nodes…' },
  results: { de: 'Ergebnisse · {n}', en: 'Results · {n}' },
  noMatch: { de: 'Kein Treffer für „{q}“', en: 'No match for "{q}"' },
  roomCount_one: { de: '{n} Raum', en: '{n} room' },
  roomCount_many: { de: '{n} Räume', en: '{n} rooms' },
  offlineSuffix: { de: ' · {n} offline', en: ' · {n} offline' },
  allHealthy: { de: ' · alle gesund', en: ' · all healthy' },
  // Rooms landing
  selectRoomTitle: { de: 'Einen Raum wählen', en: 'Select a room' },
  selectRoomMsg: {
    de: 'Wählen Sie oben einen Raum, um seine Messwerte und Steuerungen zu sehen.',
    en: 'Pick a room above to view its readings and controls.',
  },
  // Room detail
  backToRooms: { de: 'Alle Räume', en: 'All rooms' },
  growRoomTag: { de: 'Grow Room', en: 'Grow room' },
  plumbRoomTag: { de: 'Plumbing Room', en: 'Plumbing room' },
  roomReadings: { de: 'Raum-Messwerte', en: 'Room readings' },
  sectionsLabel: { de: 'Racks', en: 'Racks' },
  nodesLabel: { de: 'Nodes', en: 'Nodes' },
  noReadingsShort: { de: 'Keine Messwerte', en: 'No readings' },
  matchesRacks: { de: 'Racks / Nodes', en: 'Racks / Nodes' },
  noSections: { de: 'Noch keine Racks in diesem Raum.', en: 'No racks in this room yet.' },
  noNodes: { de: 'Noch keine Nodes in diesem Raum.', en: 'No nodes in this room yet.' },
  // Section detail
  backToRoom: { de: 'Zurück zum Raum', en: 'Back to room' },
  sectionPos: { de: 'Sektion {n} / {total}', en: 'Section {n} / {total}' },
  nodePos: { de: 'Node {n} / {total}', en: 'Node {n} / {total}' },
  prevSection: { de: 'Vorherige', en: 'Previous' },
  nextSection: { de: 'Nächste', en: 'Next' },
  offlineHead: { de: '{code} ist offline', en: '{code} is offline' },
  offlineBody: {
    de: 'Keine Live-Messwerte oder Steuerung, solange der Controller offline ist.',
    en: 'No live readings or control while the controller is offline.',
  },

  // ── F6: the Automation panel (RoomDetail, grow rooms only) ───────────────
  automationTitle: { de: 'Automatisierung', en: 'Automation' },
  automationNeedsRead: {
    de: 'Automatisierungsstatus erfordert die Berechtigung „Anbau lesen“.',
    en: 'Automation status needs Farming read permission.',
  },
  modeAuto: { de: 'Automatisch', en: 'Automatic' },
  modeManual: { de: 'Manuell', en: 'Manual' },
  modeSince: { de: 'seit {when}', en: 'since {when}' },
  // run_state — words + colour, never a bare enum on screen (§19).
  runRunning: { de: 'Läuft', en: 'Running' },
  runNotStarted: { de: 'Plan beginnt {when}', en: 'plan starts {when}' },
  runStale: {
    de: 'Der Pi läuft auf v{running} von v{issued} — löst sich innerhalb einer Minute auf',
    en: 'the Pi is on v{running} of v{issued} — resolves within a minute',
  },
  runNoBridgeReport: { de: 'Der Pi hat sich noch nie gemeldet', en: 'the Pi has never reported' },
  runBridgeHasNoPlan: { de: 'Der Pi hat keinen Plan', en: 'the Pi holds no plan' },
  runClockUntrusted: {
    de: 'Die Uhr des Pi gilt als nicht vertrauenswürdig — steuert NICHT',
    en: "the Pi's clock is untrusted — NOT driving",
  },
  runBridgeOffline: { de: 'Der Pi ist offline', en: 'the Pi is offline' },
  // settings.has_plan === false — the honest reason, per mode.
  noPlanIssued: { de: 'Kein Plan ausgestellt', en: 'No plan issued' },
  noPlanManual: { de: 'Raum ist auf manuell', en: 'room is on manual' },
  noPlanNothingPlanted: { de: 'hier ist nichts angepflanzt', en: 'nothing is planted here' },
  noPlanNoIrrigation: {
    de: 'keine Kultur an diesem Kreislauf ist zur Bewässerung geplant',
    en: 'no crop on this loop is scheduled for water',
  },
  // facts row
  factIssued: { de: 'Ausgestellt', en: 'Issued' },
  factRunning: { de: 'Läuft', en: 'Running' },
  factClock: { de: 'Uhr', en: 'Clock' },
  factLastReport: { de: 'Letzte Meldung', en: 'Last report' },
  factNextChange: { de: 'Nächste Änderung', en: 'Next change' },
  clockSynced: { de: 'synchron', en: 'synced' },
  clockHoldingOver: { de: 'überbrückt', en: 'holding over' },
  clockUntrusted: { de: 'nicht vertrauenswürdig', en: 'untrusted' },
  versionShort: { de: 'v{v}', en: 'v{v}' },
  // crops / racks
  cropsTitle: { de: 'Kulturen', en: 'Crops' },
  cropLine: { de: 'Charge {batch} · Racks {racks}', en: 'batch {batch} · racks {racks}' },
  rackLightsOn: { de: 'Licht AN', en: 'lights ON' },
  rackLightsOff: { de: 'Licht AUS', en: 'lights OFF' },
  nothingScheduledNow: { de: 'gerade nichts geplant', en: 'nothing scheduled right now' },
  ruleOn: { de: 'ein', en: 'on' },
  ruleOff: { de: 'aus', en: 'off' },
  ruleBand: {
    de: '{actuator}: ein unter {engage} → aus über {release}',
    en: '{actuator}: on below {engage} → off above {release}',
  },
  // action — take off / put back on automatic
  takeOffAutomatic: { de: 'Automatik ausschalten', en: 'Take off automatic' },
  putBackAutomatic: { de: 'Zurück auf Automatik', en: 'Put back on automatic' },
  takeOffAutomaticTitle: { de: 'Automatik ausschalten', en: 'Take off automatic' },
  takeOffAutomaticReasonHint: {
    de: 'Der Modus läuft nicht ab — der Grund ist die einzige Möglichkeit, ihn später nachzuvollziehen.',
    en: 'The mode has no expiry — the reason is the only way to trace it later.',
  },
  reasonRequired: { de: 'Grund (erforderlich)', en: 'Reason (required)' },
  planWithdrawn: {
    de: 'Plan zurückgezogen — der Raum steht unter manueller Kontrolle',
    en: 'Plan withdrawn — the room is under manual control',
  },
  modeSavedPending: {
    de: 'Modus gespeichert — wird beim nächsten Durchlauf angewendet (≤ 1 Min.)',
    en: 'Mode saved — applies on the next sweep (≤1 min)',
  },
  autoConfirmTitle: { de: 'Zurück auf Automatik?', en: 'Put back on automatic?' },
  autoConfirmMsg: {
    de: 'Der Plan wird beim nächsten Durchlauf (≤ 1 Min.) neu ausgestellt und auf die Hardware angewendet — Licht und Pumpen können dabei schalten.',
    en: 'The plan is re-issued on the next sweep (≤1 min) and applied to the hardware — lights and pumps may switch as it lands.',
  },
  modeAutoReissuing: {
    de: 'Automatik — der Plan wird beim nächsten Durchlauf neu ausgestellt',
    en: 'Automatic — the plan is re-issued on the next sweep',
  },
  // ⚠ R-356 — the lights-on inline edit is GONE, and with it `noPlanNoLightsOn`,
  // `lightsOnLabel`, `lightsOnUnset`, `lightsOnSaved` and `lightsOnSaveBtn`.
  // A photoperiod is no longer anchored on a room-wide minute of the day: each
  // crop's blocks run in minutes from the moment that crop started. The copy is
  // deleted rather than hidden — a string nothing renders is a claim waiting to
  // be rendered again by mistake.
  cancel: { de: 'Abbrechen', en: 'Cancel' },
  // D2 — a manual command's 409, named where it happened
  cmdRefusedTitle: { de: 'Befehl abgelehnt', en: 'Command refused' },
} as const satisfies Namespace;
